import React, { useState, useEffect } from 'react';
import { Building2, Mail, Phone, Loader2, Save } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { clubInfoService } from '../services/api';
import { useToast } from '../hooks/use-toast';

const ClubInfoForm = () => {
  const [clubInfo, setClubInfo] = useState({
    name: '',
    username: '',
    display_name: '',
    contact_email: '',
    contact_phone: '',
    logo_path: null
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const { toast } = useToast();

  // Load club info on component mount
  useEffect(() => {
    loadClubInfo();
  }, []);

  const loadClubInfo = async () => {
    try {
      setLoading(true);
      const response = await clubInfoService.get();
      setClubInfo(response);
    } catch (error) {
      console.error('Error loading club info:', error);
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Impossible de charger les informations du club"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setClubInfo(prev => ({
      ...prev,
      [field]: value
    }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      // Prepare update data (only modifiable fields)
      const updateData = {};
      
      if (clubInfo.display_name !== '') {
        updateData.display_name = clubInfo.display_name;
      }
      
      if (clubInfo.contact_email !== '') {
        updateData.contact_email = clubInfo.contact_email;
      }
      
      if (clubInfo.contact_phone !== '') {
        updateData.contact_phone = clubInfo.contact_phone;
      }

      await clubInfoService.update(updateData);
      
      setHasChanges(false);
      toast({
        title: "Succès",
        description: "Informations du club mises à jour"
      });
    } catch (error) {
      console.error('Error saving club info:', error);
      
      const errorMessage = error.response?.data?.detail || "Erreur lors de la sauvegarde";
      toast({
        variant: "destructive",
        title: "Erreur",
        description: errorMessage
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
        <span className="ml-2 text-gray-600">Chargement...</span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Read-only fields - Compact display */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-3 bg-gray-50 rounded-lg">
        <div className="space-y-1">
          <Label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
            Nom d'utilisateur
          </Label>
          <p className="text-sm font-medium text-gray-800">{clubInfo.username}</p>
        </div>
        
        <div className="space-y-1">
          <Label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
            Nom original
          </Label>
          <p className="text-sm font-medium text-gray-800">{clubInfo.name}</p>
        </div>
      </div>

      {/* Editable fields */}
      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="display_name" className="text-sm font-medium text-gray-700">
            Nom d'affichage *
          </Label>
          <Input
            id="display_name"
            value={clubInfo.display_name || ''}
            onChange={(e) => handleInputChange('display_name', e.target.value)}
            placeholder="Nom affiché dans l'interface"
            className="w-full"
          />
          <p className="text-xs text-gray-500">
            Ce nom apparaîtra dans le dashboard et les PDFs
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="contact_email" className="text-sm font-medium text-gray-700 flex items-center gap-2">
              <Mail className="w-4 h-4" />
              Email
            </Label>
            <Input
              id="contact_email"
              type="email"
              value={clubInfo.contact_email || ''}
              onChange={(e) => handleInputChange('contact_email', e.target.value)}
              placeholder="contact@monclub.ch"
              className="w-full"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="contact_phone" className="text-sm font-medium text-gray-700 flex items-center gap-2">
              <Phone className="w-4 h-4" />
              Téléphone
            </Label>
            <Input
              id="contact_phone"
              type="tel"
              value={clubInfo.contact_phone || ''}
              onChange={(e) => handleInputChange('contact_phone', e.target.value)}
              placeholder="+41 26 XXX XX XX"
              className="w-full"
            />
          </div>
        </div>
      </div>

      {/* Save button */}
      <div className="flex justify-end pt-3 border-t">
        <Button 
          onClick={handleSave}
          disabled={!hasChanges || saving}
          className="flex items-center gap-2"
          size="sm"
        >
          {saving ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Save className="w-4 h-4" />
          )}
          {saving ? 'Sauvegarde...' : 'Sauvegarder'}
        </Button>
      </div>
    </div>
  );
};

export default ClubInfoForm;